import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import DashLayout from "@/components/DashLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { KPI } from "@/components/DashboardWidgets";
import { Plus, Trash2, Wallet, AlertCircle, CheckCircle2, Printer, Search, Ticket, Percent, MessageCircle } from "lucide-react";
import { toast } from "sonner";

const waLink = (phone, message) => {
  if (!phone) return null;
  const digits = String(phone).replace(/\D/g, "");
  const withCc = digits.length === 10 ? `91${digits}` : digits;
  return `https://wa.me/${withCc}?text=${encodeURIComponent(message)}`;
};

const CLASSES = ["Nursery","LKG","UKG","1","2","3","4","5","6","7","8","9","10","11","12"];
const FREQS = ["one-time","monthly","quarterly","half-yearly","annual"];
const CATEGORIES = ["academic","transport","exam","library","activity","hostel","misc"];
const DISCOUNT_TYPES = ["percentage","fixed","sibling","staff","merit","scholarship","special","admission","transport"];
const MODES = ["cash","upi","card","netbanking","bank_transfer","cheque"];
const SESSIONS = ["2024-25","2025-26","2026-27","2027-28"];

const inr = (v) => `₹${(Number(v) || 0).toLocaleString("en-IN")}`;

export default function Fees() {
  const [session, setSession] = useState("2026-27");
  const [tab, setTab] = useState("dashboard");
  const [summary, setSummary] = useState({});
  const [heads, setHeads] = useState([]);
  const [structures, setStructures] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [payments, setPayments] = useState([]);
  const [discounts, setDiscounts] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [reminders, setReminders] = useState({});
  const [students, setStudents] = useState([]);
  const [filterClass, setFilterClass] = useState("all");
  const [payStatusFilter, setPayStatusFilter] = useState("all");
  const [payClassFilter, setPayClassFilter] = useState("all");
  const [searchQ, setSearchQ] = useState("");
  const [ledger, setLedger] = useState(null);
  const [ledgerOpen, setLedgerOpen] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [payFor, setPayFor] = useState(null);

  const [hForm, setHForm] = useState({ name: "", category: "academic", default_amount: "", is_mandatory: true });
  const [sForm, setSForm] = useState({ session: "2026-27", class_name: "1", section: "", head_id: "", amount: "", frequency: "monthly", due_day_of_month: 10 });
  const [applyAllClasses, setApplyAllClasses] = useState(false);
  const [dForm, setDForm] = useState({ student_id: "", d_type: "percentage", percent: "", amount: "", reason: "" });
  const [cForm, setCForm] = useState({ code: "", d_type: "fixed", value: "", valid_from: "", valid_to: "", min_amount: 0, usage_limit: 0, per_student_limit: 1 });
  const [pForm, setPForm] = useState({ mode: "cash", amount: "", txn_id: "", remarks: "", coupon_code: "" });
  const [genForm, setGenForm] = useState({ period: new Date().toISOString().slice(0, 7), class_name: "all" });

  const loadAll = async () => {
    try {
      const [s, h, st, inv, pay, dc, cp, rm, stu] = await Promise.all([
        api.get(`/fee/summary?session=${session}`),
        api.get("/fee/heads"),
        api.get(`/fee/structures?session=${session}`),
        api.get(`/fee/invoices?session=${session}&limit=500`),
        api.get("/fee/payments"),
        api.get("/fee/discounts"),
        api.get("/fee/coupons"),
        api.get("/fee/reminders"),
        api.get("/students"),
      ]);
      setSummary(s.data); setHeads(h.data); setStructures(st.data);
      setInvoices(inv.data); setPayments(pay.data); setDiscounts(dc.data);
      setCoupons(cp.data); setReminders(rm.data); setStudents(stu.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Load failed"); }
  };
  useEffect(() => { loadAll(); }, [session]); // eslint-disable-line

  const filteredInvoices = useMemo(() => invoices.filter((i) =>
    (filterClass === "all" || i.class_name === filterClass) &&
    (!searchQ || (i.student_name || "").toLowerCase().includes(searchQ.toLowerCase()))
  ), [invoices, filterClass, searchQ]);

  const addHead = async () => {
    try {
      await api.post("/fee/heads", { ...hForm, default_amount: Number(hForm.default_amount) || 0 });
      toast.success("Fee head added");
      setHForm({ name: "", category: "academic", default_amount: "", is_mandatory: true });
      loadAll();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const delHead = async (id) => { await api.delete(`/fee/heads/${id}`); loadAll(); };
  const addStructure = async () => {
    try {
      const amt = Number(sForm.amount) || 0;
      const due = Number(sForm.due_day_of_month) || 10;
      if (!sForm.head_id) { toast.error("Select a Fee Head"); return; }
      if (applyAllClasses) {
        await Promise.all(CLASSES.map((c) => api.post("/fee/structures", {
          ...sForm, class_name: c, amount: amt, due_day_of_month: due,
        })));
        toast.success(`Structure applied to all ${CLASSES.length} classes`);
      } else {
        await api.post("/fee/structures", { ...sForm, amount: amt, due_day_of_month: due });
        toast.success("Structure added");
      }
      setSForm({ ...sForm, amount: "" });
      loadAll();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const delStructure = async (id) => { await api.delete(`/fee/structures/${id}`); loadAll(); };
  const generateInvoices = async () => {
    try {
      const body = { session, period: genForm.period, class_name: genForm.class_name === "all" ? null : genForm.class_name };
      const r = await api.post("/fee/generate-invoices", body);
      toast.success(`Generated ${r.data.created} invoices (${r.data.skipped} skipped)`);
      loadAll();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const openLedger = async (student_id) => {
    try {
      const r = await api.get(`/fee/ledger/${student_id}`);
      setLedger(r.data); setLedgerOpen(true);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const openPay = (invoice) => { setPayFor(invoice); setPForm({ mode: "cash", amount: invoice.due, txn_id: "", remarks: "", coupon_code: "" }); setPayOpen(true); };
  const submitPay = async () => {
    try {
      let amount = Number(pForm.amount) || 0;
      if (pForm.coupon_code) {
        try {
          const v = await api.post("/fee/coupons/validate", { code: pForm.coupon_code, student_id: payFor.student_id, amount });
          amount = Math.max(0, amount - v.data.discount_amount);
          toast.success(`Coupon applied: ${inr(v.data.discount_amount)} off`);
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Invalid coupon"); return; }
      }
      await api.post("/fee/payments", { invoice_id: payFor.id, mode: pForm.mode, amount, txn_id: pForm.txn_id, remarks: pForm.remarks });
      toast.success("Payment recorded");
      setPayOpen(false); loadAll();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const addDiscount = async () => {
    try {
      await api.post("/fee/discounts", { ...dForm, percent: Number(dForm.percent) || 0, amount: Number(dForm.amount) || 0 });
      toast.success("Discount submitted for approval");
      setDForm({ student_id: "", d_type: "percentage", percent: "", amount: "", reason: "" });
      loadAll();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const approveDiscount = async (id, decision) => {
    await api.post(`/fee/discounts/${id}/approve?decision=${decision}`);
    toast.success(`Discount ${decision}`); loadAll();
  };
  const delDiscount = async (id) => { await api.delete(`/fee/discounts/${id}`); loadAll(); };
  const addCoupon = async () => {
    try {
      await api.post("/fee/coupons", { ...cForm, value: Number(cForm.value) || 0, min_amount: Number(cForm.min_amount) || 0, usage_limit: Number(cForm.usage_limit) || 0, per_student_limit: Number(cForm.per_student_limit) || 1, applicable_classes: [], applicable_heads: [] });
      toast.success("Coupon created");
      setCForm({ code: "", d_type: "fixed", value: "", valid_from: "", valid_to: "", min_amount: 0, usage_limit: 0, per_student_limit: 1 });
      loadAll();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const delCoupon = async (id) => { await api.delete(`/fee/coupons/${id}`); loadAll(); };
  const printReceipt = async (pid) => {
    try {
      const { data } = await api.get(`/fee/receipt/${pid}`);
      const w = window.open("", "_blank"); if (!w) return;
      const p = data.payment, i = data.invoice, s = data.student, sch = data.school;
      w.document.write(`<html><head><title>Receipt ${p.receipt_no}</title>
        <style>body{font-family:system-ui;padding:24px;max-width:640px;margin:auto;color:#0f172a}
        h1{margin:0;font-size:20px} .row{display:flex;justify-content:space-between;padding:6px 0;border-bottom:1px dashed #e2e8f0}
        .box{border:2px solid #0f172a;padding:16px;margin-top:16px} table{width:100%;border-collapse:collapse;margin-top:8px}
        th,td{padding:6px;text-align:left;border-bottom:1px solid #e2e8f0} .r{text-align:right} .total{font-weight:800;font-size:16px}</style>
        </head><body>
        <div style="display:flex;justify-content:space-between;align-items:center">
          <div><h1>${sch?.name || "School"}</h1><div>${sch?.city || ""}</div></div>
          <div style="text-align:right"><div class="total">RECEIPT</div><div>#${p.receipt_no || "—"}</div><div>${p.date}</div></div>
        </div>
        <div class="box">
          <div class="row"><b>Student</b><span>${s?.first_name || ""} ${s?.last_name || ""}</span></div>
          <div class="row"><b>Reg No</b><span>${s?.registration_no || "—"}</span></div>
          <div class="row"><b>Class</b><span>${i?.class_name || ""} ${i?.section || ""}</span></div>
          <div class="row"><b>Period</b><span>${i?.period || ""}</span></div>
          <table><thead><tr><th>Description</th><th class="r">Amount</th></tr></thead>
          <tbody>${(i?.items || []).map(x => `<tr><td>${x.head_name}</td><td class="r">${inr(x.amount)}</td></tr>`).join("")}</tbody></table>
          <div class="row"><b>Invoice Total</b><span>${inr(i?.total || 0)}</span></div>
          <div class="row"><b>Amount Paid</b><span class="total">${inr(p.amount)}</span></div>
          <div class="row"><b>Mode</b><span>${p.mode.toUpperCase()}</span></div>
          ${p.txn_id ? `<div class="row"><b>Txn ID</b><span>${p.txn_id}</span></div>` : ""}
          <div class="row"><b>Balance Due</b><span>${inr((i?.total || 0) - (i?.paid || 0))}</span></div>
        </div>
        <p style="margin-top:24px;text-align:right;color:#64748b">Authorized Signatory</p>
        <script>setTimeout(()=>window.print(),300)</script>
        </body></html>`);
      w.document.close();
    } catch (e) { toast.error("Receipt failed"); }
  };

  const studentOptions = students.filter((s) => filterClass === "all" || s.class_name === filterClass);

  return (
    <DashLayout variant="school" title="Fees ERP">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Label className="text-xs text-slate-500">Session</Label>
          <Select value={session} onValueChange={setSession}>
            <SelectTrigger className="w-40" data-testid="fee-session"><SelectValue /></SelectTrigger>
            <SelectContent>{SESSIONS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 flex-1 min-w-[280px]">
          <KPI icon={Wallet} label="Total Billed" value={inr(summary.total_billed)} color="text-blue-700 bg-blue-50" testId="fk-billed" />
          <KPI icon={CheckCircle2} label="Collected" value={inr(summary.total_paid)} color="text-emerald-700 bg-emerald-50" testId="fk-paid" />
          <KPI icon={AlertCircle} label="Due" value={inr(summary.total_due)} color="text-orange-700 bg-orange-50" testId="fk-due" />
          <KPI icon={Wallet} label="Today" value={inr(summary.today_collection)} color="text-purple-700 bg-purple-50" testId="fk-today" />
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <div className="-mx-1 overflow-x-auto pb-1">
          <TabsList className="inline-flex w-max min-w-full h-auto">
            <TabsTrigger value="dashboard" data-testid="tab-fees-dashboard">Dashboard</TabsTrigger>
            <TabsTrigger value="heads" data-testid="tab-fees-heads">Fee Heads</TabsTrigger>
            <TabsTrigger value="structure" data-testid="tab-fees-structure">Structure</TabsTrigger>
            <TabsTrigger value="invoices" data-testid="tab-fees-invoices">Invoices & Ledger</TabsTrigger>
            <TabsTrigger value="payments" data-testid="tab-fees-payments">Payments</TabsTrigger>
            <TabsTrigger value="discounts" data-testid="tab-fees-discounts">Discounts</TabsTrigger>
            <TabsTrigger value="coupons" data-testid="tab-fees-coupons">Coupons</TabsTrigger>
            <TabsTrigger value="reminders" data-testid="tab-fees-reminders">Reminders</TabsTrigger>
          </TabsList>
        </div>

        {/* Dashboard */}
        <TabsContent value="dashboard">
          <Card className="p-5">
            <h3 className="font-bold mb-3">Auto-Reminder Buckets</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              {[
                ["overdue", "Overdue", "text-red-700 bg-red-50"],
                ["due_today", "Due today", "text-amber-700 bg-amber-50"],
                ["due_in_1d", "Due tomorrow", "text-blue-700 bg-blue-50"],
                ["due_in_3d", "Due in 3d", "text-indigo-700 bg-indigo-50"],
                ["due_in_7d", "Due in 7d", "text-purple-700 bg-purple-50"],
                ["overdue_1d", "1 day past", "text-rose-700 bg-rose-50"],
                ["overdue_7d", "7 days past", "text-red-800 bg-red-50"],
              ].map(([k, l, c]) => (
                <div key={k} className={`rounded-md p-3 ${c}`}>
                  <div className="text-xs uppercase tracking-wide">{l}</div>
                  <div className="text-2xl font-black">{(reminders[k] || []).length}</div>
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>

        {/* Heads */}
        <TabsContent value="heads">
          <Card className="p-4 flex flex-wrap gap-3 items-end mb-4">
            <Input placeholder="Head name (e.g. Tuition)" value={hForm.name} onChange={(e) => setHForm({ ...hForm, name: e.target.value })} data-testid="fh-name" />
            <Select value={hForm.category} onValueChange={(v) => setHForm({ ...hForm, category: v })}>
              <SelectTrigger className="w-40" data-testid="fh-cat"><SelectValue /></SelectTrigger>
              <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
            <Input type="number" placeholder="Default amount" value={hForm.default_amount} onChange={(e) => setHForm({ ...hForm, default_amount: e.target.value })} data-testid="fh-amount" />
            <Button onClick={addHead} className="bg-blue-700 hover:bg-blue-800" data-testid="fh-add"><Plus className="w-4 h-4 mr-1" />Add Head</Button>
          </Card>
          <Card className="p-0 overflow-hidden">
            <Table><TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Category</TableHead><TableHead className="text-right">Default Amount</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
              <TableBody>{heads.map(h => (
                <TableRow key={h.id} data-testid={`head-row-${h.id}`}>
                  <TableCell className="font-semibold">{h.name}</TableCell>
                  <TableCell><Badge variant="outline">{h.category}</Badge></TableCell>
                  <TableCell className="text-right tabular-nums">{inr(h.default_amount)}</TableCell>
                  <TableCell className="text-right"><Button size="sm" variant="ghost" onClick={() => delHead(h.id)}><Trash2 className="w-4 h-4 text-red-600" /></Button></TableCell>
                </TableRow>
              ))}
                {heads.length === 0 && <TableRow><TableCell colSpan={4} className="text-center py-8 text-slate-500">Add your first fee head above</TableCell></TableRow>}
              </TableBody></Table>
          </Card>
        </TabsContent>

        {/* Structure */}
        <TabsContent value="structure">
          <Card className="p-4 grid grid-cols-2 md:grid-cols-7 gap-2 items-end mb-4">
            <Select value={sForm.session} onValueChange={(v) => setSForm({ ...sForm, session: v })}>
              <SelectTrigger data-testid="fs-session"><SelectValue /></SelectTrigger>
              <SelectContent>{SESSIONS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={sForm.class_name} onValueChange={(v) => setSForm({ ...sForm, class_name: v })} disabled={applyAllClasses}>
              <SelectTrigger data-testid="fs-class"><SelectValue placeholder="Class" /></SelectTrigger>
              <SelectContent>{CLASSES.map(c => <SelectItem key={c} value={c}>Class {c}</SelectItem>)}</SelectContent>
            </Select>
            <Input placeholder="Section (optional)" value={sForm.section} onChange={(e) => setSForm({ ...sForm, section: e.target.value })} data-testid="fs-section" />
            <Select value={sForm.head_id} onValueChange={(v) => setSForm({ ...sForm, head_id: v })}>
              <SelectTrigger data-testid="fs-head"><SelectValue placeholder="Fee Head" /></SelectTrigger>
              <SelectContent>{heads.map(h => <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>)}</SelectContent>
            </Select>
            <Input type="number" placeholder="Amount" value={sForm.amount} onChange={(e) => setSForm({ ...sForm, amount: e.target.value })} data-testid="fs-amount" />
            <Select value={sForm.frequency} onValueChange={(v) => setSForm({ ...sForm, frequency: v })}>
              <SelectTrigger data-testid="fs-freq"><SelectValue /></SelectTrigger>
              <SelectContent>{FREQS.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
            </Select>
            <Button onClick={addStructure} className="bg-blue-700 hover:bg-blue-800 col-span-2 md:col-span-1" data-testid="fs-add"><Plus className="w-4 h-4 mr-1" />{applyAllClasses ? "Apply to All" : "Add"}</Button>
            <label className="flex items-center gap-2 text-xs text-slate-600 col-span-2 md:col-span-7 -mt-1 cursor-pointer" data-testid="fs-apply-all-wrapper">
              <input type="checkbox" checked={applyAllClasses} onChange={(e) => setApplyAllClasses(e.target.checked)} data-testid="fs-apply-all" />
              <span>Apply this fee head to <b>all classes</b> in one click (great for one-time / annual fees)</span>
            </label>
          </Card>
          <Card className="p-0 overflow-hidden">
            <Table><TableHeader><TableRow><TableHead>Session</TableHead><TableHead>Class</TableHead><TableHead>Head</TableHead><TableHead className="text-right">Amount</TableHead><TableHead>Frequency</TableHead><TableHead>Due</TableHead><TableHead className="text-right">—</TableHead></TableRow></TableHeader>
              <TableBody>{structures.map(x => { const h = heads.find(y => y.id === x.head_id); return (
                <TableRow key={x.id}>
                  <TableCell>{x.session}</TableCell>
                  <TableCell>{x.class_name}{x.section ? ` ${x.section}` : ""}</TableCell>
                  <TableCell>{h?.name || x.head_id.slice(-6)}</TableCell>
                  <TableCell className="text-right tabular-nums font-semibold">{inr(x.amount)}</TableCell>
                  <TableCell><Badge variant="outline">{x.frequency}</Badge></TableCell>
                  <TableCell>Day {x.due_day_of_month}</TableCell>
                  <TableCell className="text-right"><Button size="sm" variant="ghost" onClick={() => delStructure(x.id)}><Trash2 className="w-4 h-4 text-red-600" /></Button></TableCell>
                </TableRow>);})}
                {structures.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-500">No fee structure yet</TableCell></TableRow>}
              </TableBody></Table>
          </Card>
        </TabsContent>

        {/* Invoices */}
        <TabsContent value="invoices">
          <Card className="p-4 flex flex-wrap gap-3 items-end mb-4">
            <Input type="month" value={genForm.period} onChange={(e) => setGenForm({ ...genForm, period: e.target.value })} data-testid="fg-period" />
            <Select value={genForm.class_name} onValueChange={(v) => setGenForm({ ...genForm, class_name: v })}>
              <SelectTrigger className="w-40" data-testid="fg-class"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">All Classes</SelectItem>{CLASSES.map(c => <SelectItem key={c} value={c}>Class {c}</SelectItem>)}</SelectContent>
            </Select>
            <Button onClick={generateInvoices} className="bg-emerald-700 hover:bg-emerald-800" data-testid="fg-generate">Generate Invoices for Period</Button>
            <div className="ml-auto flex items-center gap-2">
              <Search className="w-4 h-4 text-slate-400" />
              <Input placeholder="Search student name" value={searchQ} onChange={(e) => setSearchQ(e.target.value)} className="w-56" data-testid="fi-search" />
              <Select value={filterClass} onValueChange={setFilterClass}>
                <SelectTrigger className="w-36" data-testid="fi-class"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="all">All Classes</SelectItem>{CLASSES.map(c => <SelectItem key={c} value={c}>Class {c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </Card>
          <Card className="p-0 overflow-hidden">
            {/* Desktop table */}
            <div className="hidden md:block">
              <Table><TableHeader><TableRow><TableHead>Student</TableHead><TableHead>Class</TableHead><TableHead>Period</TableHead><TableHead className="text-right">Total</TableHead><TableHead className="text-right">Paid</TableHead><TableHead className="text-right">Due</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
                <TableBody>{filteredInvoices.map(i => (
                  <TableRow key={i.id} data-testid={`inv-row-${i.id}`}>
                    <TableCell className="font-semibold cursor-pointer hover:underline text-blue-700" onClick={() => openLedger(i.student_id)}>{i.student_name}</TableCell>
                    <TableCell>{i.class_name}{i.section ? ` ${i.section}` : ""}</TableCell>
                    <TableCell className="font-mono text-xs">{i.period}</TableCell>
                    <TableCell className="text-right tabular-nums">{inr(i.total)}</TableCell>
                    <TableCell className="text-right tabular-nums text-emerald-700">{inr(i.paid)}</TableCell>
                    <TableCell className="text-right tabular-nums text-red-700 font-semibold">{inr(i.due)}</TableCell>
                    <TableCell><Badge variant="outline" className={i.status === "paid" ? "border-emerald-300 text-emerald-700" : i.status === "partial" ? "border-amber-300 text-amber-700" : "border-red-300 text-red-700"}>{i.status}</Badge></TableCell>
                    <TableCell className="text-right space-x-1">
                      <Button size="sm" disabled={i.due <= 0} onClick={() => openPay(i)} data-testid={`inv-pay-${i.id}`}>Collect</Button>
                      {i.due > 0 && (() => {
                        const stu = students.find(x => x.id === i.student_id);
                        const phone = stu?.parent_details?.father?.mobile || stu?.parent_details?.mother?.mobile || stu?.preferred_mob;
                        const msg = `Dear Parent,\n\nWith due respect, this is a gentle reminder regarding pending school fees for ${i.student_name} (Class ${i.class_name || ""}${i.section ? " " + i.section : ""}, ${i.period}).\n\nOutstanding: *₹${(i.due).toLocaleString("en-IN")}*\nDue Date: ${i.due_date}\n\nKindly clear the dues at your earliest convenience.\n\nThank you.`;
                        const url = waLink(phone, msg);
                        return <Button size="sm" variant="outline" disabled={!url} onClick={() => url && window.open(url, "_blank")} data-testid={`inv-wa-${i.id}`} title={phone ? "WhatsApp reminder" : "No parent phone on file"}><MessageCircle className="w-4 h-4 text-emerald-600" /></Button>;
                      })()}
                    </TableCell>
                  </TableRow>))}
                  {filteredInvoices.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-8 text-slate-500">No invoices — click Generate above</TableCell></TableRow>}
                </TableBody></Table>
            </div>
            {/* Mobile card list */}
            <div className="md:hidden divide-y divide-slate-100">
              {filteredInvoices.map(i => {
                const stu = students.find(x => x.id === i.student_id);
                const phone = stu?.parent_details?.father?.mobile || stu?.parent_details?.mother?.mobile || stu?.preferred_mob;
                const msg = `Dear Parent,\n\nWith due respect, this is a gentle reminder regarding pending school fees for ${i.student_name} (Class ${i.class_name || ""}${i.section ? " " + i.section : ""}, ${i.period}).\n\nOutstanding: *₹${(i.due).toLocaleString("en-IN")}*\nDue Date: ${i.due_date}\n\nKindly clear the dues at your earliest convenience.\n\nThank you.`;
                const url = waLink(phone, msg);
                return (
                  <div key={i.id} className="p-4" data-testid={`inv-card-${i.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="font-semibold text-blue-700 cursor-pointer" onClick={() => openLedger(i.student_id)}>{i.student_name}</div>
                        <div className="text-xs text-slate-500">Class {i.class_name}{i.section ? ` ${i.section}` : ""} · {i.period}</div>
                      </div>
                      <Badge variant="outline" className={i.status === "paid" ? "border-emerald-300 text-emerald-700" : i.status === "partial" ? "border-amber-300 text-amber-700" : "border-red-300 text-red-700"}>{i.status}</Badge>
                    </div>
                    <div className="grid grid-cols-3 gap-2 mt-3 text-center">
                      <div className="rounded bg-slate-50 py-1.5"><div className="text-[10px] uppercase text-slate-500">Total</div><div className="text-sm font-bold tabular-nums">{inr(i.total)}</div></div>
                      <div className="rounded bg-emerald-50 py-1.5"><div className="text-[10px] uppercase text-emerald-700">Paid</div><div className="text-sm font-bold tabular-nums text-emerald-700">{inr(i.paid)}</div></div>
                      <div className="rounded bg-red-50 py-1.5"><div className="text-[10px] uppercase text-red-700">Due</div><div className="text-sm font-bold tabular-nums text-red-700">{inr(i.due)}</div></div>
                    </div>
                    <div className="flex gap-2 mt-3">
                      <Button size="sm" className="flex-1 bg-blue-700 hover:bg-blue-800" disabled={i.due <= 0} onClick={() => openPay(i)} data-testid={`inv-card-pay-${i.id}`}>Collect</Button>
                      {i.due > 0 && <Button size="sm" variant="outline" className="flex-1" disabled={!url} onClick={() => url && window.open(url, "_blank")} data-testid={`inv-card-wa-${i.id}`}><MessageCircle className="w-4 h-4 mr-1 text-emerald-600" />Remind</Button>}
                    </div>
                  </div>
                );
              })}
              {filteredInvoices.length === 0 && <div className="text-center py-8 text-slate-500 text-sm">No invoices — click Generate above</div>}
            </div>
          </Card>
        </TabsContent>

        {/* Payments */}
        <TabsContent value="payments">
          <Card className="p-3 flex flex-wrap items-center gap-3 mb-3">
            <Select value={payClassFilter} onValueChange={setPayClassFilter}>
              <SelectTrigger className="w-36" data-testid="pay-filter-class"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Classes</SelectItem>
                {CLASSES.map((c) => <SelectItem key={c} value={c}>Class {c}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={payStatusFilter} onValueChange={setPayStatusFilter}>
              <SelectTrigger className="w-40" data-testid="pay-filter-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="paid">Fully Paid</SelectItem>
                <SelectItem value="partial">Partial / Dues</SelectItem>
                <SelectItem value="pending">Pending / No Payment</SelectItem>
              </SelectContent>
            </Select>
            <div className="text-xs text-slate-500 ml-auto" data-testid="pay-count">
              Showing {(() => {
                const filtered = payments.filter((p) => {
                  const stu = students.find((x) => x.id === p.student_id);
                  const inv = invoices.find((i) => i.id === p.invoice_id);
                  const invStatus = inv?.status || p.status;
                  if (payStatusFilter !== "all" && invStatus !== payStatusFilter) return false;
                  if (payClassFilter !== "all" && stu?.class_name !== payClassFilter) return false;
                  return true;
                });
                return filtered.length;
              })()} of {payments.length}
            </div>
          </Card>
          {(() => {
            const payFiltered = payments.filter((p) => {
              const stu = students.find((x) => x.id === p.student_id);
              const inv = invoices.find((i) => i.id === p.invoice_id);
              const invStatus = inv?.status || p.status;
              if (payStatusFilter !== "all" && invStatus !== payStatusFilter) return false;
              if (payClassFilter !== "all" && stu?.class_name !== payClassFilter) return false;
              return true;
            });
            return (
          <Card className="p-0 overflow-hidden">
            {/* Desktop table */}
            <div className="hidden md:block">
              <Table><TableHeader><TableRow><TableHead>Receipt</TableHead><TableHead>Date</TableHead><TableHead>Student</TableHead><TableHead>Mode</TableHead><TableHead className="text-right">Amount</TableHead><TableHead>Status</TableHead><TableHead className="text-right">—</TableHead></TableRow></TableHeader>
                <TableBody>{payFiltered.map(p => { const s = students.find(x => x.id === p.student_id); return (
                  <TableRow key={p.id} data-testid={`pay-row-${p.id}`}>
                    <TableCell className="font-mono text-xs">{p.receipt_no || "—"}</TableCell>
                    <TableCell>{p.date}</TableCell>
                    <TableCell>{s ? `${s.first_name} ${s.last_name}` : p.student_id.slice(-6)}</TableCell>
                    <TableCell><Badge variant="outline">{p.mode}</Badge></TableCell>
                    <TableCell className="text-right tabular-nums font-semibold">{inr(p.amount)}</TableCell>
                    <TableCell><Badge variant="outline" className={p.status === "paid" ? "border-emerald-300 text-emerald-700" : "border-amber-300 text-amber-700"}>{p.status}</Badge></TableCell>
                    <TableCell className="text-right">{p.status === "paid" && <Button size="sm" variant="outline" onClick={() => printReceipt(p.id)} data-testid={`pay-print-${p.id}`}><Printer className="w-4 h-4 mr-1" />Receipt</Button>}</TableCell>
                  </TableRow>);})}
                  {payFiltered.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-500">No payments match the filters</TableCell></TableRow>}
                </TableBody></Table>
            </div>
            {/* Mobile card list */}
            <div className="md:hidden divide-y divide-slate-100">
              {payFiltered.map(p => { const s = students.find(x => x.id === p.student_id); return (
                <div key={p.id} className="p-4" data-testid={`pay-card-${p.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-semibold">{s ? `${s.first_name} ${s.last_name}` : p.student_id.slice(-6)}</div>
                      <div className="text-xs text-slate-500 font-mono">{p.receipt_no || "—"} · {p.date}</div>
                    </div>
                    <Badge variant="outline" className={p.status === "paid" ? "border-emerald-300 text-emerald-700" : "border-amber-300 text-amber-700"}>{p.status}</Badge>
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <div className="text-lg font-bold tabular-nums">{inr(p.amount)} <Badge variant="outline" className="ml-1">{p.mode}</Badge></div>
                    {p.status === "paid" && <Button size="sm" variant="outline" onClick={() => printReceipt(p.id)} data-testid={`pay-card-print-${p.id}`}><Printer className="w-4 h-4 mr-1" />Receipt</Button>}
                  </div>
                </div>);})}
              {payFiltered.length === 0 && <div className="text-center py-8 text-slate-500 text-sm">No payments match the filters</div>}
            </div>
          </Card>
            );
          })()}
        </TabsContent>

        {/* Discounts */}
        <TabsContent value="discounts">
          <Card className="p-4 grid grid-cols-2 md:grid-cols-6 gap-2 items-end mb-4">
            <Select value={dForm.student_id} onValueChange={(v) => setDForm({ ...dForm, student_id: v })}>
              <SelectTrigger className="col-span-2" data-testid="fd-student"><SelectValue placeholder="Student" /></SelectTrigger>
              <SelectContent className="max-h-72">{students.map(s => <SelectItem key={s.id} value={s.id}>{s.first_name} {s.last_name} · Class {s.class_name}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={dForm.d_type} onValueChange={(v) => setDForm({ ...dForm, d_type: v })}>
              <SelectTrigger data-testid="fd-type"><SelectValue /></SelectTrigger>
              <SelectContent>{DISCOUNT_TYPES.map(x => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
            </Select>
            <Input type="number" placeholder="Percent" value={dForm.percent} onChange={(e) => setDForm({ ...dForm, percent: e.target.value })} data-testid="fd-percent" />
            <Input type="number" placeholder="Fixed ₹" value={dForm.amount} onChange={(e) => setDForm({ ...dForm, amount: e.target.value })} data-testid="fd-amount" />
            <Button onClick={addDiscount} className="bg-blue-700 hover:bg-blue-800" data-testid="fd-add"><Percent className="w-4 h-4 mr-1" />Submit</Button>
            <Textarea placeholder="Reason (optional)" className="col-span-6" rows={2} value={dForm.reason} onChange={(e) => setDForm({ ...dForm, reason: e.target.value })} data-testid="fd-reason" />
          </Card>
          <Card className="p-0 overflow-hidden">
            <Table><TableHeader><TableRow><TableHead>Student</TableHead><TableHead>Type</TableHead><TableHead>Value</TableHead><TableHead>Reason</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
              <TableBody>{discounts.map(d => { const s = students.find(x => x.id === d.student_id); return (
                <TableRow key={d.id} data-testid={`disc-row-${d.id}`}>
                  <TableCell>{s ? `${s.first_name} ${s.last_name}` : d.student_id.slice(-6)}</TableCell>
                  <TableCell><Badge variant="outline">{d.d_type}</Badge></TableCell>
                  <TableCell className="tabular-nums">{d.percent ? `${d.percent}%` : ""}{d.amount ? ` ${inr(d.amount)}` : ""}</TableCell>
                  <TableCell className="text-xs text-slate-500">{d.reason}</TableCell>
                  <TableCell><Badge variant="outline" className={d.status === "approved" ? "border-emerald-300 text-emerald-700" : d.status === "rejected" ? "border-red-300 text-red-700" : "border-amber-300 text-amber-700"}>{d.status}</Badge></TableCell>
                  <TableCell className="text-right space-x-1">
                    {d.status === "pending" && <>
                      <Button size="sm" onClick={() => approveDiscount(d.id, "approved")} data-testid={`disc-approve-${d.id}`}>Approve</Button>
                      <Button size="sm" variant="outline" onClick={() => approveDiscount(d.id, "rejected")} data-testid={`disc-reject-${d.id}`}>Reject</Button>
                    </>}
                    <Button size="sm" variant="ghost" onClick={() => delDiscount(d.id)}><Trash2 className="w-4 h-4 text-red-600" /></Button>
                  </TableCell>
                </TableRow>);})}
                {discounts.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-500">No discounts</TableCell></TableRow>}
              </TableBody></Table>
          </Card>
        </TabsContent>

        {/* Coupons */}
        <TabsContent value="coupons">
          <Card className="p-4 grid grid-cols-2 md:grid-cols-7 gap-2 items-end mb-4">
            <Input placeholder="CODE" value={cForm.code} onChange={(e) => setCForm({ ...cForm, code: e.target.value.toUpperCase() })} data-testid="fc-code" />
            <Select value={cForm.d_type} onValueChange={(v) => setCForm({ ...cForm, d_type: v })}>
              <SelectTrigger data-testid="fc-type"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="fixed">Fixed ₹</SelectItem><SelectItem value="percent">Percent %</SelectItem></SelectContent>
            </Select>
            <Input type="number" placeholder="Value" value={cForm.value} onChange={(e) => setCForm({ ...cForm, value: e.target.value })} data-testid="fc-value" />
            <Input type="date" value={cForm.valid_from} onChange={(e) => setCForm({ ...cForm, valid_from: e.target.value })} data-testid="fc-from" />
            <Input type="date" value={cForm.valid_to} onChange={(e) => setCForm({ ...cForm, valid_to: e.target.value })} data-testid="fc-to" />
            <Input type="number" placeholder="Usage limit" value={cForm.usage_limit} onChange={(e) => setCForm({ ...cForm, usage_limit: e.target.value })} data-testid="fc-limit" />
            <Button onClick={addCoupon} className="bg-blue-700 hover:bg-blue-800" data-testid="fc-add"><Ticket className="w-4 h-4 mr-1" />Create</Button>
          </Card>
          <Card className="p-0 overflow-hidden">
            <Table><TableHeader><TableRow><TableHead>Code</TableHead><TableHead>Type</TableHead><TableHead className="text-right">Value</TableHead><TableHead>Valid</TableHead><TableHead>Used / Limit</TableHead><TableHead className="text-right">—</TableHead></TableRow></TableHeader>
              <TableBody>{coupons.map(c => (
                <TableRow key={c.id} data-testid={`coup-row-${c.id}`}>
                  <TableCell className="font-mono font-semibold">{c.code}</TableCell>
                  <TableCell><Badge variant="outline">{c.d_type}</Badge></TableCell>
                  <TableCell className="text-right tabular-nums">{c.d_type === "fixed" ? inr(c.value) : `${c.value}%`}</TableCell>
                  <TableCell className="text-xs">{c.valid_from} → {c.valid_to}</TableCell>
                  <TableCell>{c.used_count || 0} / {c.usage_limit || "∞"}</TableCell>
                  <TableCell className="text-right"><Button size="sm" variant="ghost" onClick={() => delCoupon(c.id)}><Trash2 className="w-4 h-4 text-red-600" /></Button></TableCell>
                </TableRow>))}
                {coupons.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-500">No coupons</TableCell></TableRow>}
              </TableBody></Table>
          </Card>
        </TabsContent>

        {/* Reminders */}
        <TabsContent value="reminders">
          {["overdue", "due_today", "due_in_1d", "due_in_3d", "due_in_7d", "overdue_1d", "overdue_7d"].map(k => (
            <Card key={k} className="p-4 mb-3">
              <h3 className="font-bold mb-2 capitalize">{k.replace(/_/g, " ")} ({(reminders[k] || []).length})</h3>
              <div className="text-sm space-y-1">
                {(reminders[k] || []).slice(0, 10).map(i => (
                  <div key={i.id} className="flex justify-between border-b pb-1">
                    <span>{i.student_name} · Class {i.class_name}</span>
                    <span className="tabular-nums text-red-700 font-semibold">{inr(i.due)}</span>
                  </div>
                ))}
                {(reminders[k] || []).length === 0 && <div className="text-slate-400 text-xs">Empty</div>}
              </div>
            </Card>
          ))}
        </TabsContent>
      </Tabs>

      {/* Ledger dialog */}
      <Dialog open={ledgerOpen} onOpenChange={setLedgerOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle>Student Fee Ledger</DialogTitle></DialogHeader>
          {ledger && <div className="space-y-3">
            <div className="grid grid-cols-4 gap-2 text-sm">
              <div><div className="text-xs text-slate-500">Total Fee</div><div className="font-bold text-lg tabular-nums">{inr(ledger.total_fee)}</div></div>
              <div><div className="text-xs text-slate-500">Discount</div><div className="font-bold text-lg tabular-nums text-amber-700">{inr(ledger.discount)}</div></div>
              <div><div className="text-xs text-slate-500">Paid</div><div className="font-bold text-lg tabular-nums text-emerald-700">{inr(ledger.paid)}</div></div>
              <div><div className="text-xs text-slate-500">Due</div><div className="font-bold text-lg tabular-nums text-red-700">{inr(ledger.due)}</div></div>
            </div>
            <div className="border rounded max-h-80 overflow-y-auto">
              <Table><TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Description</TableHead><TableHead className="text-right">Charge</TableHead><TableHead className="text-right">Paid</TableHead></TableRow></TableHeader>
                <TableBody>{ledger.ledger.map((e, i) => (
                  <TableRow key={i}>
                    <TableCell className="font-mono text-xs">{e.date}</TableCell>
                    <TableCell className="text-sm">{e.description}</TableCell>
                    <TableCell className="text-right tabular-nums">{e.amount ? inr(e.amount) : "—"}</TableCell>
                    <TableCell className="text-right tabular-nums text-emerald-700">{e.paid ? inr(e.paid) : "—"}</TableCell>
                  </TableRow>))}</TableBody></Table>
            </div>
          </div>}
        </DialogContent>
      </Dialog>

      {/* Pay dialog */}
      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Collect Fee — {payFor?.student_name}</DialogTitle></DialogHeader>
          {payFor && <div className="space-y-3">
            <div className="text-sm">Invoice: {inr(payFor.total)} · Paid: {inr(payFor.paid)} · <b className="text-red-700">Due: {inr(payFor.due)}</b></div>
            <div className="grid grid-cols-2 gap-2">
              <Select value={pForm.mode} onValueChange={(v) => setPForm({ ...pForm, mode: v })}>
                <SelectTrigger data-testid="pay-mode"><SelectValue /></SelectTrigger>
                <SelectContent>{MODES.map(m => <SelectItem key={m} value={m}>{m.toUpperCase()}</SelectItem>)}</SelectContent>
              </Select>
              <Input type="number" placeholder="Amount" value={pForm.amount} onChange={(e) => setPForm({ ...pForm, amount: e.target.value })} data-testid="pay-amount" />
              {pForm.mode !== "cash" && <Input placeholder="Txn / Ref no." value={pForm.txn_id} onChange={(e) => setPForm({ ...pForm, txn_id: e.target.value })} className="col-span-2" data-testid="pay-txn" />}
              <Input placeholder="Coupon code (optional)" value={pForm.coupon_code} onChange={(e) => setPForm({ ...pForm, coupon_code: e.target.value.toUpperCase() })} className="col-span-2" data-testid="pay-coupon" />
              <Textarea placeholder="Remarks" rows={2} value={pForm.remarks} onChange={(e) => setPForm({ ...pForm, remarks: e.target.value })} className="col-span-2" data-testid="pay-remarks" />
            </div>
            <DialogFooter><Button onClick={submitPay} className="bg-emerald-700 hover:bg-emerald-800" data-testid="pay-submit">Record Payment</Button></DialogFooter>
          </div>}
        </DialogContent>
      </Dialog>
    </DashLayout>
  );
}
